import { acceptHMRUpdate, defineStore } from 'pinia'
import { get, set, del, keys } from 'idb-keyval'

function check_valid(data: null | undefined | { [key: string]: any }) {
  if (data === null || data === undefined || data.value === undefined) {
    return false
  }
  return !('expiry' in data && new Date().getTime() > data.expiry)
}

export const useCacheStore = defineStore('cache', {
  state: () => ({
    cache: new Map<string, { [key: string]: any }>(),
  }),
  actions: {
    async setItem(dat: { [key: string]: any }) {
      const now = new Date().getTime()

      const v = {
        timestamp: now,
        value: dat.value,
        expiry: now + (dat.expiry || 300) * 1000,
      }

      // prune old keys
      keys().then(async (resp) => {
        for (const k of resp) {
          if (k.toString().includes('__expiry__')) {
            const exp = await get(k)
            if (exp && exp < now) {
              await this.clearItem(k.toString().replace('__expiry__', ''))
              await this.clearItem(k.toString())
            }
          }
        }
      })

      await set(dat.key, JSON.stringify(v))
      await set(dat.key + '__expiry__', now + (dat.expiry || 300) * 1000)
      this.$patch((state) => {
        state.cache.set(dat.key, Object.freeze(v))
      })
    },
    async clearItem(key: string) {
      if (key in this.$state.cache) {
        this.$patch((state) => {
          state.cache.delete(key)
        })
      }
      await del(key)
    },
    async get(input: string | { [key: string]: any }) {
      let payload: { [key: string]: any } = {}
      if (typeof input === 'string') {
        payload.key = input
        payload.url = input
      } else {
        payload = input
      }
      // Sends a get request to the passed location and caches the response
      // pass either a URL or an object.
      // Object should contain the keys: 'key', 'url', 'expiry' (in seconds), 'config' (passed to $api)
      if (!('key' in payload)) {
        payload.key = payload.url
      }
      if (payload.key in this.$state.cache) {
        const ret = this.$state.cache.get(payload.key)
        if (ret && check_valid(ret)) {
          return ret.value
        }
      } else {
        const ret = await get(payload.key)
        if (ret) {
          const parsed = JSON.parse(ret)
          if (check_valid(parsed)) {
            this.$patch((state) => {
              state.cache.set(parsed.key, parsed.value)
            })
            payload.value = parsed.value
            return payload.value
          }
        }
      }

      if ('url' in payload) {
        const config = payload.config || {}
        config.method = config.method || 'GET'
        const resp = await $api(payload.url, config).catch((err) => {
          throw err
        })

        payload.value = resp
        await this.setItem(payload)
        return resp
      }
    },
  },
})

if (import.meta.hot) {
  import.meta.hot.accept(acceptHMRUpdate(useCacheStore, import.meta.hot))
}
